import axios from 'axios';
import Echo from 'laravel-echo';
import Pusher from 'pusher-js';
import { isInChromeExtension } from 'utils/proxyUtils';

declare global {
  interface Window {
    Pusher: typeof Pusher;
    Echo: Echo<'reverb'>;
  }
}

window.Pusher = Pusher;
// Pusher.logToConsole = true;

type EchoClientState = {
  instance: Echo<'reverb'> | null;
  initializing: boolean;
};

const state: EchoClientState = {
  instance: null,
  initializing: false,
};

const env = {
  appKey:
    import.meta.env.VITE_REVERB_APP_KEY || process.env.CEB_VITE_REVERB_APP_KEY,
  host: import.meta.env.VITE_REVERB_HOST || process.env.CEB_VITE_REVERB_HOST,
  port: import.meta.env.VITE_REVERB_PORT || process.env.CEB_VITE_REVERB_PORT,
  baseUrl:
    import.meta.env.VITE_APP_BASE_URL || process.env.CEB_VITE_APP_BASE_URL,
  scheme:
    import.meta.env.VITE_REVERB_SCHEME ||
    process.env.CEB_VITE_REVERB_SCHEME ||
    'https',
};

async function getExtensionToken(): Promise<string | null> {
  return new Promise((resolve) => {
    try {
      (
        window as unknown as {
          chrome?: {
            runtime?: {
              sendMessage?: (
                msg: unknown,
                cb: (resp: { token?: string } | null) => void
              ) => void;
              lastError?: unknown;
            };
          };
        }
      )?.chrome?.runtime?.sendMessage?.(
        { type: 'GET_AUTH_TOKEN' },
        (response: { token?: string } | null) => {
          const anyWin = window as unknown as {
            chrome?: { runtime?: { lastError?: unknown } };
          };
          if (anyWin?.chrome?.runtime?.lastError) {
            resolve(null);
          } else {
            resolve(response?.token || null);
          }
        }
      );
    } catch (_e) {
      resolve(null);
    }
  });
}

function buildEcho(
  authorizeWithBearer: () => Promise<string | null>
): Echo<'reverb'> {
  return new Echo({
    broadcaster: 'reverb',
    key: env.appKey,
    wsHost: env.host,
    wsPort: env.port,
    wssPort: env.port,
    authorizer: (channel) => {
      return {
        authorize: async (socketId, callback) => {
          try {
            const headers: Record<string, string> = {
              'Content-Type': 'application/json',
              Accept: 'application/json',
            };

            const bearer = await authorizeWithBearer();
            if (bearer) headers['Authorization'] = `Bearer ${bearer}`;

            const response = await axios.post(
              `${env.baseUrl}/broadcasting/auth`,
              { socket_id: socketId, channel_name: channel.name },
              { headers, withCredentials: true }
            );
            callback(null, response.data);
          } catch (error) {
            callback(error as Error, null);
          }
        },
      };
    },
    forceTLS: `${env.scheme}` === 'https',
    enabledTransports: ['ws', 'wss'],
    withCredentials: true,
  });
}

export async function initializeRealtime(): Promise<Echo<'reverb'>> {
  if (state.instance) return state.instance;
  if (state.initializing) {
    // Busy wait until ready (short):
    return new Promise((resolve) => {
      const id = setInterval(() => {
        if (state.instance) {
          clearInterval(id);
          resolve(state.instance);
        }
      }, 50);
    });
  }
  state.initializing = true;

  const isExt = isInChromeExtension();
  const tokenSupplier = async () => (isExt ? await getExtensionToken() : null);

  const echo = buildEcho(tokenSupplier);
  state.instance = echo;
  window.Echo = echo;
  state.initializing = false;

  return echo;
}

export function getEcho(): Echo<'reverb'> | null {
  return state.instance || window.Echo || null;
}

export function disconnectRealtime(): void {
  try {
    const echo = getEcho() as unknown as {
      connector?: { pusher?: { disconnect?: () => void } };
      disconnect?: () => void;
    } | null;
    echo?.connector?.pusher?.disconnect?.();
    echo?.disconnect?.();
  } catch (_e) {
    // noop
  } finally {
    state.instance = null;
    // keep type safety for delete
    delete (window as { Echo?: Echo<'reverb'> }).Echo;
  }
}

export async function reinitializeRealtime(): Promise<Echo<'reverb'>> {
  disconnectRealtime();
  return initializeRealtime();
}
